import { motion } from "framer-motion";
import { MapPin, Phone, Clock, Baby, Bath } from "lucide-react";
import { IMAGES } from "@/lib/api";

const hours = [
  ["Monday", "Closed"],
  ["Tuesday", "9 AM – 5 PM"],
  ["Wednesday", "9 AM – 5 PM"],
  ["Thursday", "9 AM – 5 PM"],
  ["Friday", "8 AM – 5 PM"],
  ["Saturday", "8 AM – 5 PM"],
  ["Sunday", "Closed"],
];

export const About = () => {
  return (
    <section id="about" className="py-24 sm:py-32 bg-barber-bg">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative"
          >
            <img
              src={IMAGES.tools}
              alt="Barber tools at The Barber Parlor"
              className="w-full h-[460px] object-cover object-center border border-barber-gold/20"
            />
            <img
              src={IMAGES.beard}
              alt="Beard trim in progress"
              className="hidden sm:block absolute -bottom-10 -right-6 w-48 h-56 object-cover border-4 border-barber-bg shadow-2xl"
            />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <p className="uppercase tracking-[0.35em] text-barber-gold text-xs mb-4">The Shop</p>
            <h2 className="font-heading text-4xl sm:text-5xl font-bold text-barber-cream tracking-tight">
              Craftsmanship in Every Cut
            </h2>
            <p className="mt-6 text-barber-muted leading-relaxed">
              Tucked away on Prescott Street in Worcester, The Barber Parlor is where the
              classic barbershop experience lives on. Great conversation, an excellent
              atmosphere, and a barber who takes the time to get it right. Come once and
              you'll understand why folks make us their personal barber.
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              <span className="flex items-center gap-2 bg-barber-surface border border-barber-gold/20 px-4 py-2 text-sm text-barber-cream">
                <Baby className="w-4 h-4 text-barber-gold" /> Good for kids
              </span>
              <span className="flex items-center gap-2 bg-barber-surface border border-barber-gold/20 px-4 py-2 text-sm text-barber-cream">
                <Bath className="w-4 h-4 text-barber-gold" /> Restroom
              </span>
            </div>

            <div className="mt-10 grid sm:grid-cols-2 gap-8">
              <div>
                <h3 className="font-heading text-xl text-barber-gold mb-3">Visit Us</h3>
                <p className="flex items-start gap-2 text-barber-muted text-sm mb-2">
                  <MapPin className="w-4 h-4 text-barber-gold mt-0.5 shrink-0" />
                  170 Prescott St Ste 2, Worcester, MA 01605
                </p>
                <a href="tel:+17742322759" className="flex items-center gap-2 text-barber-muted text-sm hover:text-barber-gold transition-colors">
                  <Phone className="w-4 h-4 text-barber-gold" /> (774) 232-2759
                </a>
              </div>
              <div>
                <h3 className="font-heading text-xl text-barber-gold mb-3 flex items-center gap-2">
                  <Clock className="w-4 h-4" /> Hours
                </h3>
                <ul className="space-y-1">
                  {hours.map(([d, h]) => (
                    <li key={d} className="flex justify-between text-sm text-barber-muted">
                      <span>{d}</span>
                      <span className={h === "Closed" ? "text-barber-red/90" : "text-barber-cream"}>{h}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </motion.div>
        </div>

        <div className="mt-20 border border-barber-gold/20">
          <iframe
            title="The Barber Parlor location"
            data-testid="map-embed"
            src="https://www.google.com/maps?q=170+Prescott+St+Ste+2,+Worcester,+MA+01605&output=embed"
            className="w-full h-80 grayscale-[0.3]"
            loading="lazy"
          />
        </div>
      </div>
    </section>
  );
};
